/**
 * Teslyn's own email from Teslyn Cloud (batch 6, F14; docs/specs/cloud/app-email.md): who a message can go to, and
 * sending it through `email_send`, which checks the sender, the people and the limits, and queues it for the notify
 * function (from "No Reply <organisation> powered by Teslyn", replies to the sender). The dialog lists names only; the database finds the
 * addresses.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  emailRecipientChoices,
  recipientIds,
  type EmailRecipientChoice,
  type RecipientPerson,
  type RecipientSignIn,
  type ReportEmailSetting,
} from '@teslyn/domain';
import { plainPortalError, type Res } from '../portal/api.ts';

function must<T>(r: Res<T>): T {
  if (r.error) throw new Error(plainPortalError(r.error));
  return r.data as T;
}

type UserJoin = { email: string | null; display_name: string; system?: boolean } | null;
function one<T>(v: T | T[] | null | undefined): T | null {
  return Array.isArray(v) ? (v[0] ?? null) : (v ?? null);
}

/** The people a message about this project can go to (`emailRecipientChoices`). */
export async function loadRecipients(
  sb: SupabaseClient,
  tenantId: string,
  projectId: string | null,
  clientOrgId: string | null,
): Promise<EmailRecipientChoice[]> {
  const [people, crew, staff, clients] = await Promise.all([
    sb.from('person').select('id, name, email, user_id, active, deleted_at').eq('tenant_id', tenantId),
    projectId
      ? sb.from('project_crew').select('person_id, role, deleted_at').eq('project_id', projectId)
      : Promise.resolve({ data: [], error: null }),
    sb
      .from('membership')
      .select('user_id, app_user:user_id (email, display_name, system)')
      .eq('tenant_id', tenantId),
    clientOrgId
      ? sb
          .from('client_membership')
          .select('user_id, app_user:user_id (email, display_name), client_org:client_org_id (name)')
          .eq('client_org_id', clientOrgId)
      : Promise.resolve({ data: [], error: null }),
  ]);
  const signIns: RecipientSignIn[] = [];
  const seen = new Set<string>();
  for (const m of must(staff as Res<{ user_id: string; app_user: UserJoin | UserJoin[] }[]>) ?? []) {
    const u = one(m.app_user);
    if (!u || u.system || seen.has(m.user_id)) continue;
    seen.add(m.user_id);
    signIns.push({
      user_id: m.user_id,
      name: u.display_name || u.email || 'Unnamed sign-in',
      email: u.email,
      client_org: null,
    });
  }
  type ClientRow = {
    user_id: string;
    app_user: UserJoin | UserJoin[];
    client_org: { name: string } | { name: string }[] | null;
  };
  for (const m of must(clients as Res<ClientRow[]>) ?? []) {
    const u = one(m.app_user);
    signIns.push({
      user_id: m.user_id,
      name: u?.display_name || u?.email || 'A client',
      email: u?.email ?? null,
      client_org: one(m.client_org)?.name ?? 'Client',
    });
  }
  return emailRecipientChoices({
    people: (must(people as Res<RecipientPerson[]>) ?? []) as RecipientPerson[],
    crew: must(crew as Res<{ person_id: string; role: string; deleted_at: string | null }[]>) ?? [],
    signIns,
  });
}

export interface EmailSent {
  readonly message_id: string;
  readonly queued: number;
  readonly without_email: readonly string[];
}

/** Send a message to the chosen people (`person:<id>` and `user:<id>` keys). */
export async function sendEmail(
  sb: SupabaseClient,
  m: {
    readonly tenantId: string;
    readonly projectId: string | null;
    readonly keys: readonly string[];
    readonly subject: string;
    readonly body: string;
  },
): Promise<EmailSent> {
  const ids = recipientIds(m.keys);
  return must(
    (await sb.rpc('email_send', {
      p_tenant: m.tenantId,
      p_project: m.projectId,
      p_people: ids.people,
      p_users: ids.users,
      p_subject: m.subject,
      p_body: m.body,
    })) as Res<EmailSent>,
  );
}

/** The organisation's name, for the sender "No Reply <organisation> powered by Teslyn" (decision 890); null if unread. */
export async function loadTenantName(sb: SupabaseClient, tenantId: string): Promise<string | null> {
  const r = (await sb.from('tenant').select('name').eq('id', tenantId).maybeSingle()) as Res<{
    name: string;
  } | null>;
  return r.error ? null : (r.data?.name ?? null);
}

/**
 * A project's report emails (`project_report_email`, decisions 883, 884): every column, so a database before
 * 20261030100000 still answers (its rows have daily and weekly only). Null: no row, Teslyn's defaults.
 */
export async function loadReportEmailSetting(
  sb: SupabaseClient,
  projectId: string,
): Promise<ReportEmailSetting | null> {
  return (
    must(
      (await sb
        .from('project_report_email')
        .select('*')
        .eq('project_id', projectId)
        .maybeSingle()) as Res<ReportEmailSetting | null>,
    ) ?? null
  );
}

/** One email of the outbox as the Emails sent tab shows it (app-email.md §12): never its body. */
export interface EmailLogRow {
  readonly id: string;
  readonly kind: 'report_published' | 'message';
  readonly toName: string;
  readonly toEmail: string;
  /** The message's subject, or the report email's subject as sent; '' when the row predates the words. */
  readonly subject: string;
  readonly status: 'pending' | 'sent' | 'failed' | 'cancelled';
  readonly attempts: number;
  readonly error: string | null;
  readonly createdAt: string;
  readonly sentAt: string | null;
  readonly projectId: string | null;
}

/**
 * The columns the Emails sent tab reads: who, the subject, when and the state. Named, never `*`: the outbox also
 * holds each message's words (`body`, `email_message`), which the tab never asks for.
 */
export const EMAIL_LOG_COLUMNS =
  'id, kind, to_name, to_email, subject, email_subject, status, attempts, error, created_at, sent_at, project_id';

/** At most this many emails are listed (the newest). */
export const EMAIL_LOG_LIMIT = 500;

interface EmailLogRaw {
  id: string;
  kind: 'report_published' | 'message';
  to_name: string | null;
  to_email: string;
  subject: string | null;
  email_subject: string | null;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  attempts: number | null;
  error: string | null;
  created_at: string;
  sent_at: string | null;
  project_id: string | null;
}

/**
 * The organisation's outbox, newest first (tenant admins read every row, `email_outbox_select`; others their own
 * Email… messages, which the tab is not offered to).
 */
export async function loadEmailLog(sb: SupabaseClient, tenantId: string): Promise<EmailLogRow[]> {
  const rows =
    must(
      (await sb
        .from('email_outbox')
        .select(EMAIL_LOG_COLUMNS)
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false })
        .range(0, EMAIL_LOG_LIMIT - 1)) as Res<EmailLogRaw[]>,
    ) ?? [];
  return rows.map((r) => ({
    id: r.id,
    kind: r.kind,
    toName: r.to_name ?? '',
    toEmail: r.to_email,
    subject: r.subject ?? r.email_subject ?? '',
    status: r.status,
    attempts: r.attempts ?? 0,
    error: r.error,
    createdAt: r.created_at,
    sentAt: r.sent_at,
    projectId: r.project_id,
  }));
}
